import { useEffect, useRef } from "react";
import flatpickr from "flatpickr";
import { Korean } from "flatpickr/dist/l10n/ko.js";
import "flatpickr/dist/flatpickr.min.css";
import "./DatePicker.css";

// Calendar SVG (lucide:calendar, 16px) — matches prototype .fp-input-icon
const CALENDAR_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>`;

export interface DatePickerProps {
	/** React Hook Form register result */
	register: ReturnType<
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		(...args: any[]) => {
			ref: React.RefCallback<HTMLInputElement>;
			onChange: React.ChangeEventHandler<HTMLInputElement>;
			onBlur: React.ChangeEventHandler<HTMLInputElement>;
			name: string;
		}
	>;
	className?: string;
	placeholder?: string;
	disabled?: boolean;
	/** Flatpickr date format (default: "Y-m-d" for date, "Y-m-d h:i K" for datetime) */
	dateFormat?: string;
	/** Enable time picker (adds hour/minute/AM-PM controls below calendar) */
	enableTime?: boolean;
}

/**
 * Flatpickr-powered date input.
 *
 * Wraps a hidden native `<input type="date">` so React Hook Form stays
 * in sync, then renders flatpickr's calendar UI on click.
 *
 * Visual: `.fp-input-wrap > input + .fp-input-icon` (styled in DatePicker.css).
 */
export function DatePicker({
	register,
	className = "input",
	placeholder,
	disabled,
	dateFormat,
	enableTime = false,
}: DatePickerProps) {
	const inputRef = useRef<HTMLInputElement | null>(null);
	const fpRef = useRef<flatpickr.Instance | null>(null);
	const wrapRef = useRef<HTMLDivElement | null>(null);

	// Default format: datetime uses Korean AM/PM; date uses Y-m-d
	const fmt = dateFormat ?? (enableTime ? "Y-m-d h:i K" : "Y-m-d");

	// Combine RHF register ref with our own ref; stash onChange for flatpickr
	const { ref: rhfRef, onChange: rhfOnChange, ...rhfRest } = register;

	// Keep rhfOnChange in a ref so flatpickr callback always has latest
	const rhfOnChangeRef = useRef(rhfOnChange);
	rhfOnChangeRef.current = rhfOnChange;

	const combinedRef = (el: HTMLInputElement | null) => {
		inputRef.current = el;
		if (typeof rhfRef === "function") {
			rhfRef(el);
		}
	};

	// Initialize flatpickr on mount
	useEffect(() => {
		const input = inputRef.current;
		if (!input) return;

		const fp = flatpickr(input, {
			locale: Korean,
			dateFormat: fmt,
			enableTime,
			time_24hr: false,
			allowInput: true,
			disableMobile: true,
			onChange: (_selectedDates, _dateStr, instance) => {
				// Notify React Hook Form that the value changed
				const el = instance.input;
				const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
					window.HTMLInputElement.prototype,
					"value",
				)?.set;
				if (nativeInputValueSetter) {
					nativeInputValueSetter.call(el, el.value);
					el.dispatchEvent(new Event("input", { bubbles: true }));
				}
				// Also call RHF's onChange handler directly
				rhfOnChangeRef.current?.({
					target: el,
					type: "change",
				} as React.ChangeEvent<HTMLInputElement>);
			},
		});

		fpRef.current = fp;

		return () => {
			fp.destroy();
			fpRef.current = null;
		};
	}, [fmt, enableTime]);

	// ── Render: wrapper > input + calendar icon ──
	return (
		<div ref={wrapRef} className="fp-input-wrap">
			<input
				type={enableTime ? "text" : "date"}
				ref={combinedRef}
				{...rhfRest}
				className={className}
				placeholder={placeholder}
				disabled={disabled}
				data-fp-wrapped="1"
				autoComplete="off"
			/>
			<span
				className="fp-input-icon"
				dangerouslySetInnerHTML={{ __html: CALENDAR_SVG }}
			/>
		</div>
	);
}
